import { AutocompleteType } from '../../../types'
import {
  Box,
  Button,
  Stack,
  Switch,
  TextArea,
  TextField,
  Tooltip,
  Typography
} from '@dtx-company/design-system/src'
import { ContactCollection } from '@dtx-company/shared-components/src/foundation/Icons/Flowpage/Widgets/ContactCollection'
import { ContactFormState, ContactInfoDisplayTypes, LocationProps, ToggleProps } from './types'
import { ContactInfoDisplayToggle } from './ContactInfoDisplayToggle'
import {
  ContactInfoFields,
  contactInfoFormTransforms,
  getLabelForContactInput,
  parseTypes
} from './utils'
import { ContactInfoInput } from './ContactInfoInput'
import { ContactInfoLinkStyleForm } from './subcomponents/ContactInfoLinkStyleForm'
import { ContactPageOutlined, HelpOutlineOutlined, LocationCityOutlined } from '@mui/icons-material'
import { FC, useEffect, useRef } from 'react'
import { FormProps, StyledForm, StyledFormV2 } from '../components/Layout'
import { FormTitle } from '../components/Title'
import { LinkStyleGated } from '../components/LinkStyle/LinkStyleGated'
import { indexInterfaceWithVar } from '@dtx-company/true-common/src/utils/objects'
import { useContactForm } from './hooks'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Save from '@mui/icons-material/Save'
import useScript from '../../../utils/useScript'

export const AllowDownloadingVCardToggle: FC<ToggleProps> = ({
  setValueAndPreview,
  resetDisplayTypeValueWhenUnchecked,
  value,
  disabled
}: ToggleProps) => (
  <Box sx={{ marginLeft: '11px' }}>
    <Switch
      disabled={disabled}
      label={
        <Stack direction="row" alignItems="center" justifyContent="center" gap={2}>
          <Tooltip
            placement="top"
            id="share-contact-card"
            title="When enabled, this will allow visitors to your Flowpage to click on 'add to contacts' and the above information can be saved to their phone's contacts."
          >
            <HelpOutlineOutlined />
          </Tooltip>
          <Typography variant="body2">
            Allow your visitors to download your virtual contact card.
          </Typography>
        </Stack>
      }
      checked={value}
      onChange={e => {
        const { checked } = e.target
        setValueAndPreview(ContactInfoFields.CARD, checked)
        if (!checked) resetDisplayTypeValueWhenUnchecked?.()
      }}
    />
  </Box>
)

export const LocationForm: FC<LocationProps> = ({ setValue, value }: LocationProps) => {
  const autoCompleteRef = useRef<HTMLInputElement | null>(null)

  const [loaded, error] = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_CLOUD_API_KEY}&libraries=places`
  )

  useEffect(() => {
    if (loaded && !error) {
      let autoComplete: AutocompleteType | undefined

      const handlePlaceSelect = async (): Promise<void> => {
        const addressObject = autoComplete?.getPlace()
        const query = addressObject?.formatted_address
        if (query) {
          const address = addressObject?.address_components?.reduce(
            (acc, curr) => {
              const key = parseTypes(curr.types)
              return {
                ...acc,
                ...(key && { [key]: curr.long_name })
              }
            },
            {} as Record<string, string> | null
          )
          setValue(ContactInfoFields.LOCATION, query)
          if (address && query) {
            address?.street1 && setValue(ContactInfoFields.STREET1, address.street1)
            address?.street2 && setValue(ContactInfoFields.STREET2, address.street2)
            address?.state && setValue(ContactInfoFields.STATE, address.state)
            address?.city && setValue(ContactInfoFields.CITY, address.city)
            address?.zip_code && setValue(ContactInfoFields.ZIPCODE, address.zip_code)
          }
        }
      }

      const handleScriptLoad = (): void => {
        if (window.google && autoCompleteRef.current) {
          const el = document.getElementById('location-gmaps-contact-form')
          autoComplete = new window.google.maps.places.Autocomplete(autoCompleteRef.current)
          autoComplete?.setFields(['address_components', 'formatted_address'])
          autoComplete?.addListener('place_changed', handlePlaceSelect)
          if (el) {
            window.google.maps.event.addDomListener(el, 'keydown', function (event) {
              if ((event as KeyboardEvent).key === 'Enter') {
                event.preventDefault()
              }
            })
          }
        }
      }

      handleScriptLoad()
    }
  }, [error, loaded, setValue])

  return (
    <TextField
      onChange={e => {
        if (e.target.value === '') {
          setValue(ContactInfoFields.LOCATION, '')
          setValue(ContactInfoFields.STREET1, '')
          setValue(ContactInfoFields.STREET2, '')
          setValue(ContactInfoFields.STATE, '')
          setValue(ContactInfoFields.CITY, '')
          setValue(ContactInfoFields.ZIPCODE, '')
        }
      }}
      fullWidth
      type="text"
      defaultValue={value}
      error={error}
      inputProps={{
        id: 'location-gmaps-contact-form',
        ref: autoCompleteRef
      }}
      placeholder="123 Main St, New York, NY 10001"
      label={getLabelForContactInput('LOCATION') ?? 'location'}
      startAdornment={<LocationCityOutlined />}
    />
  )
}

export const ContactForm: FC<FormProps> = (props: FormProps) => {
  const {
    mobile,
    disableCard,
    watchAll,
    contactInputs,
    handleCountryCodeChange,
    setValueAndPreview,
    errors,
    handleSubmit,
    codes,
    initialEmpty,
    onSubmit,
    clearContactInfo,
    clearErrors
  } = useContactForm(props)
  const edit = !initialEmpty

  return (
    <StyledForm
      title={
        <FormTitle
          icon={<ContactCollection />}
          title={`${edit ? 'Edit' : 'Add'} your contact info`}
        />
      }
      onSubmit={handleSubmit(onSubmit)}
      subheader={
        <Typography variant="body1">
          Add your contact information for instant connections with people you meet, your fans, and
          your customers
        </Typography>
      }
      noPreview
    >
      {contactInputs.map((type, idx) => {
        const lowerType = type.toLowerCase() as ContactInfoFields
        return (
          <ContactInfoInput
            mobile={mobile}
            key={idx}
            handleCountryCodeChange={handleCountryCodeChange}
            type={type as ContactInfoFields}
            onChange={e => {
              clearErrors(lowerType)
              setValueAndPreview(
                lowerType,
                contactInfoFormTransforms[lowerType]?.(e.target.value) ?? e.target.value
              )
            }}
            errors={errors}
            codes={codes}
            value={String(indexInterfaceWithVar(watchAll, lowerType) || '')}
          />
        )
      })}

      <LocationForm
        setValue={(name, value) => setValueAndPreview(name as ContactInfoFields, value)}
        value={watchAll?.location || ''}
      />

      <TextArea
        fullWidth
        placeholder={'Title'}
        helperText={errors?.title?.message}
        value={watchAll.title || ''}
        error={Boolean(errors?.title?.message)}
        label={'Title'}
        onChange={e => {
          setValueAndPreview(ContactInfoFields.TITLE, e.target.value)
        }}
      />

      <TextArea
        fullWidth
        placeholder={'Company'}
        helperText={errors?.company?.message}
        value={watchAll.company || ''}
        error={Boolean(errors?.company?.message)}
        label={'Company'}
        onChange={e => {
          setValueAndPreview(ContactInfoFields.COMPANY, e.target.value)
        }}
      />

      <Stack justifyContent="space-between" flexDirection={['column', 'column', 'row']}>
        <ContactInfoDisplayToggle
          setValue={setValueAndPreview}
          displayType={watchAll.displayType}
        />
        <AllowDownloadingVCardToggle
          disabled={disableCard}
          setValueAndPreview={setValueAndPreview}
          value={Boolean(indexInterfaceWithVar(watchAll, ContactInfoFields.CARD))}
        />
      </Stack>

      <Stack
        flexDirection={['column', 'column', 'row']}
        justifyContent="space-between"
        width="100%"
        gap={2}
      >
        <Button fullWidth type="submit">
          Save contact info
        </Button>

        <Button fullWidth variant="outlined" type="button" onClick={clearContactInfo}>
          Delete contact info
        </Button>
      </Stack>
    </StyledForm>
  )
}

export const InfoFormContent: FC<ContactFormState> = ({
  contactInputs,
  mobile,
  handleCountryCodeChange,
  setValueAndPreview,
  errors,
  clearErrors,
  codes,
  watchAll,
  disableCard,
  countryCodeState
}) => (
  <Stack gap={4}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={4}>
      <TextField
        fullWidth
        placeholder="John"
        value={watchAll.firstName}
        label="First Name"
        onChange={e => {
          setValueAndPreview(ContactInfoFields.FIRST_NAME, e.target.value)
        }}
      />
      <TextField
        fullWidth
        placeholder="Doe"
        value={watchAll.lastName}
        label="Last Name"
        onChange={e => {
          setValueAndPreview(ContactInfoFields.LAST_NAME, e.target.value)
        }}
      />
    </Stack>
    {contactInputs.map((type, idx) => {
      const lowerType = type.toLowerCase() as ContactInfoFields
      const label = getLabelForContactInput(type)
      return (
        <ContactInfoInput
          mobile={mobile}
          key={idx}
          handleCountryCodeChange={handleCountryCodeChange}
          type={type as ContactInfoFields}
          onChange={e => {
            clearErrors?.(lowerType)
            setValueAndPreview(
              lowerType,
              contactInfoFormTransforms[lowerType]?.(e.target.value) ?? e.target.value,
              {
                shouldValidate: true
              }
            )
          }}
          errors={errors}
          codes={codes}
          label={label}
          value={String(indexInterfaceWithVar(watchAll, lowerType) || '')}
          countryCodeState={countryCodeState}
        />
      )
    })}
    <LocationForm
      setValue={(name, value) => setValueAndPreview(name as ContactInfoFields, value)}
      value={watchAll?.location || ''}
    />

    <TextArea
      fullWidth
      placeholder={'Title'}
      helperText={errors?.title?.message}
      value={watchAll.title || ''}
      error={Boolean(errors?.title?.message)}
      label={'Title'}
      onChange={e => {
        setValueAndPreview(ContactInfoFields.TITLE, e.target.value)
      }}
    />

    <TextArea
      fullWidth
      placeholder={'Company'}
      helperText={errors?.company?.message}
      value={watchAll.company || ''}
      error={Boolean(errors?.company?.message)}
      label={'Company'}
      onChange={e => {
        setValueAndPreview(ContactInfoFields.COMPANY, e.target.value)
      }}
    />

    <AllowDownloadingVCardToggle
      disabled={disableCard}
      setValueAndPreview={setValueAndPreview}
      resetDisplayTypeValueWhenUnchecked={() => {
        // If user unchecked the "Allow visitors to download vCard" and the current displayType
        // was ContactInfoDisplayTypes.LINK_CARD, reset displayType to ContactInfoDisplayTypes.DEFAULT
        // because we only allow displaying the contactInfo link as a link-card when
        // "Allow visitors to download vCard" is checked (on)
        if (watchAll.displayType === ContactInfoDisplayTypes.LINK_CARD)
          setValueAndPreview(ContactInfoFields.DISPLAY_TYPE, ContactInfoDisplayTypes.DEFAULT)
      }}
      value={Boolean(indexInterfaceWithVar(watchAll, ContactInfoFields.CARD))}
    />
  </Stack>
)

export const ContactInfoFormV2: FC<FormProps> = ({ handleClose, order, widgetObj, curr }) => {
  const formProps = useContactForm({ handleClose, order, widgetObj, curr })

  return (
    <StyledFormV2 onSubmit={formProps.handleSubmit(formProps.onSubmit)}>
      <Stack gap={4}>
        <FormTitle
          icon={<ContactPageOutlined />}
          title={`${formProps.curr ? 'Edit' : 'Add'} contact information`}
        />

        <InfoFormContent {...formProps} />

        <ContactInfoLinkStyleForm
          watchAll={formProps.watchAll}
          setValueAndPreview={formProps.setValueAndPreview}
        />

        <Box>
          <LinkStyleGated
            hiddenFields={{
              shadowColor: true,
              textSize: true,
              borderColor: true,
              fontColor: true
            }}
            linkTheme={formProps.linkTheme}
            setLinkTheme={formProps.setLinkTheme}
          />
        </Box>

        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Button fullWidth onClick={formProps.clearContactInfo} startIcon={DeleteOutline}>
            Delete Link
          </Button>
          <Button
            fullWidth
            startIcon={Save}
            color="tertiary"
            data-test="link-form-submit"
            type="submit"
          >
            Save Link
          </Button>
        </Stack>
      </Stack>
    </StyledFormV2>
  )
}
